import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { fetchActiveBranding, WEKIT_DEFAULTS, type BrandingProfile } from "@/lib/demoBranding";

/**
 * Meeting Mode™ — Cinematic Welcome
 * Pulls the active demo branding profile and plays a 5s intro that segues
 * into the Institution Dashboard. Respects prefers-reduced-motion.
 */
export default function Meeting() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<BrandingProfile | null>(null);
  const [phase, setPhase] = useState<"loading" | "intro" | "greet" | "done">("loading");
  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  useEffect(() => {
    fetchActiveBranding().then((p) => {
      setProfile(p);
      setPhase("intro");
    });
  }, []);

  useEffect(() => {
    if (phase !== "intro") return;
    if (reduced) {
      setPhase("greet");
      const t = setTimeout(() => setPhase("done"), 800);
      return () => clearTimeout(t);
    }
    const t1 = setTimeout(() => setPhase("greet"), 2600);
    const t2 = setTimeout(() => setPhase("done"), 5000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [phase, reduced]);

  useEffect(() => {
    if (phase === "done") navigate("/institution", { replace: true });
  }, [phase, navigate]);

  const primary = profile?.primary_color || WEKIT_DEFAULTS.primary;
  const secondary = profile?.secondary_color || WEKIT_DEFAULTS.secondary;
  const accent = profile?.accent_color || WEKIT_DEFAULTS.accent;
  const school = profile?.school_name || WEKIT_DEFAULTS.school_name;
  const principal = profile?.principal_name?.trim();
  const year = profile?.academic_year;
  const tagline = profile?.tagline || WEKIT_DEFAULTS.tagline;
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  return (
    <>
      <PageHead title={`${school} · Meeting Mode | WeKIT™`} description="Cinematic institutional welcome" path="/meeting" />
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
        style={{
          background: `radial-gradient(ellipse at 30% 20%, ${primary}22, transparent 60%), radial-gradient(ellipse at 70% 80%, ${secondary}22, transparent 60%), #0b0f1a`,
        }}
      >
        {/* Animated root/tree */}
        <svg
          className="absolute inset-0 w-full h-full opacity-40"
          viewBox="0 0 800 600"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden
        >
          <defs>
            <linearGradient id="rootGrad" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor={primary} stopOpacity="0.9" />
              <stop offset="100%" stopColor={accent} stopOpacity="0.3" />
            </linearGradient>
          </defs>
          {[...Array(8)].map((_, i) => (
            <path
              key={i}
              d={`M400 600 Q ${300 + i * 30} ${400 - i * 20} ${200 + i * 60} ${100 + (i % 3) * 40}`}
              stroke="url(#rootGrad)"
              strokeWidth="2"
              fill="none"
              style={{
                strokeDasharray: 800,
                strokeDashoffset: reduced ? 0 : 800,
                animation: reduced ? "none" : `dash 2.4s ease-out ${i * 0.08}s forwards`,
              }}
            />
          ))}
        </svg>

        <style>{`
          @keyframes dash { to { stroke-dashoffset: 0; } }
          @keyframes riseIn { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
          @keyframes softPulse { 0%,100% { opacity: 0.85; } 50% { opacity: 1; } }
        `}</style>

        <div className="relative z-10 text-center px-6 max-w-3xl">
          {phase === "intro" && (
            <div style={{ animation: reduced ? undefined : "riseIn 900ms ease-out both" }}>
              {profile?.logo_url ? (
                <img src={profile.logo_url} alt={school} className="mx-auto h-24 w-auto mb-8 drop-shadow-lg" />
              ) : (
                <div
                  className="mx-auto h-24 w-24 mb-8 rounded-2xl flex items-center justify-center text-3xl font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
                >
                  {school.slice(0, 1)}
                </div>
              )}
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
                {school}
              </h1>
              <p className="text-lg md:text-xl text-white/70" style={{ animation: reduced ? undefined : "softPulse 2.4s ease-in-out infinite" }}>
                {tagline}
              </p>
            </div>
          )}

          {phase === "greet" && (
            <div style={{ animation: reduced ? undefined : "riseIn 700ms ease-out both" }}>
              <p className="uppercase tracking-[0.3em] text-xs mb-4" style={{ color: accent }}>
                {today}{year ? ` · Academic Year ${year}` : ""}
              </p>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                {principal ? `Welcome, ${principal}` : "Welcome"}
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-2">
                Your {school} intelligence dashboard is ready.
              </p>
              <p className="text-sm text-white/50">Powered by WeKIT Career Clarity 360™</p>
            </div>
          )}

          <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 flex gap-3">
            {phase === "greet" && (
              <Button
                size="sm"
                className="bg-white text-black hover:bg-white/90"
                onClick={() => setPhase("done")}
              >
                Enter Institution Dashboard →
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-white/60 hover:text-white hover:bg-white/10"
              onClick={() => setPhase("done")}
            >
              Skip animation
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
